import {
  HeidiVersion,
  ModalId,
  TeamEvidenceAccessMode,
  type components,
} from '@heidi/schema/ml-scribe';
import { ENTITLEMENT } from '@heidi/workspace/contracts';

/** Schema calls it `BillingInfoResponseView`; commerce aliases it `UserBillingInfo`. */
type UserBillingInfo = components['schemas']['BillingInfoResponseView'];

type TeamEvidenceEntitlement = {
  name?: string | null;
  enabled?: boolean | null;
};

export type FullTeamEvidenceAccessInput = {
  /** False while joined-team data has not settled. */
  isTeamMembershipKnown: boolean;
  hasTeam: boolean;
  /**
   * Team entitlement list. `undefined` while that query is loading or failed.
   * A loaded list with no `evidence` row is not a deny.
   */
  teamEntitlements: readonly TeamEvidenceEntitlement[] | undefined;
  accessMode: TeamEvidenceAccessMode | null | undefined;
  roleIdsCanAccessEvidence: readonly (string | null | undefined)[] | null | undefined;
  /**
   * Every role on the team. `undefined` while that query is loading or failed.
   * Only consulted when `roleIdsCanAccessEvidence` is non-empty.
   */
  teamRoleIds: readonly (string | null | undefined)[] | null | undefined;
};

function isEvidenceEntitlementExplicitlyDisabled(
  entitlements: readonly TeamEvidenceEntitlement[]
): boolean {
  return entitlements.some((entitlement) => {
    return (
      entitlement.name?.toLowerCase() === ENTITLEMENT.EVIDENCE &&
      entitlement.enabled !== true
    );
  });
}

function presentRoleIds(
  ids: readonly (string | null | undefined)[] | null | undefined
): string[] {
  return (ids ?? []).filter(
    (id): id is string => typeof id === 'string' && id.length > 0
  );
}

/**
 * Empty allow-list is the API's unrestricted sentinel. Selecting every role
 * persists the full id list instead, and that is still everyone.
 * A non-empty list with no resolved catalog stays restricted.
 */
function isEveryTeamRoleAllowed(
  roleIdsCanAccessEvidence: FullTeamEvidenceAccessInput['roleIdsCanAccessEvidence'],
  teamRoleIds: FullTeamEvidenceAccessInput['teamRoleIds']
): boolean {
  const allowedRoleIds = presentRoleIds(roleIdsCanAccessEvidence);
  if (allowedRoleIds.length === 0) return true;

  const roles = presentRoleIds(teamRoleIds);
  if (roles.length === 0) return false;

  const allowed = new Set(allowedRoleIds);
  return roles.every((id) => allowed.has(id));
}

/**
 * Heidi 2 is for teams where everyone has Evidence.
 * No team skips this gate. An explicit team deny, or a by-role list that
 * leaves a role out, is not full access. BY_MEMBER can revoke individuals
 * and has no cheap team-level answer, so those teams are gated off.
 */
export function computeHasFullTeamEvidenceAccess(
  input: FullTeamEvidenceAccessInput
): boolean {
  if (!input.isTeamMembershipKnown) return false;
  if (!input.hasTeam) return true;
  if (input.teamEntitlements === undefined) return false;
  if (isEvidenceEntitlementExplicitlyDisabled(input.teamEntitlements)) return false;

  if (
    (input.accessMode ?? TeamEvidenceAccessMode.BY_ROLE) ===
    TeamEvidenceAccessMode.BY_MEMBER
  ) {
    return false;
  }

  return isEveryTeamRoleAllowed(input.roleIdsCanAccessEvidence, input.teamRoleIds);
}

export type Heidi2OptInGate = {
  isHeidi2OptInEnabled: boolean;
  /** False during workspace-switch reload. */
  isWorkspaceIdSettled: boolean;
  /** Includes paused + Chronicle. */
  isClinicalRecordingActive: boolean;
  /** `user.is_managed`; unknown while loading. */
  isUserManaged: boolean | null | undefined;
  /** `JoinedWorkspaceRelation.user_role === 'ADMIN'`; false while workspace is loading. */
  isWorkspaceAdmin: boolean;
  /** Whole-team Evidence is unrestricted. False while that status is unresolved. */
  hasFullTeamEvidenceAccess: boolean;
  /**
   * `ModalId.CLINICIAN_TRIAL` is showing (`currentQueuedModalAtom`) or is the
   * queue head before tier and pacing gates (`admittedModalQueueAtom[0]`).
   * The banner is unqueued T4 chrome and would otherwise stack on that T3
   * trial modal.
   */
  isClinicianTrialModalBlocking: boolean;
  /**
   * The commerce verdict on the seat, as an argument: a Max seat is already on
   * everything the banner would offer. Passed in rather than imported — the
   * commerce `.` door is a foreign domain and this zone may not read one.
   */
  isMaxBillingSeat: (
    billingInfo: Pick<UserBillingInfo, 'seat_type'> | null | undefined
  ) => boolean;
  /**
   * Launch queue has evaluated with user data, seen-modal ids and billing
   * loaded. `CLINICIAN_TRIAL` is enqueued in that same derivation; reading the
   * queue before then sees an empty list and this surface flashes first.
   */
  isClinicianTrialEligibilityLoaded: boolean;
};

export type Heidi2OptInBillingInfo = Pick<
  UserBillingInfo,
  'seat_type' | 'new_product_tier' | 'heidi_version' | 'third_party_license'
> & {
  evidence_billing_state?: Pick<
    NonNullable<UserBillingInfo['evidence_billing_state']>,
    'can_start_evidence_trial'
  > | null;
};

export function isClinicianTrialModalBlockingHeidi2OptIn({
  currentQueuedModalId,
  queueHeadModalId,
}: {
  currentQueuedModalId: ModalId | null | undefined;
  queueHeadModalId: ModalId | null | undefined;
}): boolean {
  return (
    currentQueuedModalId === ModalId.CLINICIAN_TRIAL ||
    queueHeadModalId === ModalId.CLINICIAN_TRIAL
  );
}

/**
 * Billing half shared by the admin opt-in strip and the non-admin request strip.
 * Same exclusions, so a cohort the admin cannot activate never gets a dead-end
 * "Request access" either.
 */
export function isHeidi1BillingEligibleForHeidi2(
  billingInfo: Heidi2OptInBillingInfo | null | undefined,
  isMaxBillingSeat: Heidi2OptInGate['isMaxBillingSeat']
): boolean {
  if (billingInfo == null) {
    return false;
  }
  // Backend refuses activate-heidi-2 for EHR-partner licenses (Stripe-only).
  if (billingInfo.third_party_license != null) {
    return false;
  }
  // Claimable evidence trial is offered Heidi 2 by CLINICIAN_TRIAL
  // (`activateHeidi2ClinicianTrial`). Backend owns this boolean; missing
  // state is all-false, so the banner may show.
  if (billingInfo.evidence_billing_state?.can_start_evidence_trial) {
    return false;
  }
  if (isMaxBillingSeat(billingInfo)) {
    return false;
  }
  return billingInfo.heidi_version === HeidiVersion.Value1;
}

/**
 * Heidi 1 opt-in cohort: workspace admin, not already on a Max seat.
 * Inline chrome applies T4 R1/R2 itself; R4 is `MODAL_CLOSED_RULES`.
 */
export function computeShouldShowHeidi2OptIn(
  gate: Heidi2OptInGate,
  billingInfo: Heidi2OptInBillingInfo | null | undefined
): boolean {
  if (!gate.isClinicianTrialEligibilityLoaded) {
    return false;
  }
  if (!gate.isWorkspaceIdSettled) {
    return false;
  }
  if (!gate.isHeidi2OptInEnabled) {
    return false;
  }
  if (gate.isClinicalRecordingActive) {
    return false;
  }
  if (gate.isClinicianTrialModalBlocking) {
    return false;
  }
  // Unknown (`undefined`/`null`) counts as managed — no flash on enterprise.
  if (gate.isUserManaged !== false) {
    return false;
  }
  if (!gate.isWorkspaceAdmin) {
    return false;
  }
  if (!gate.hasFullTeamEvidenceAccess) {
    return false;
  }
  return isHeidi1BillingEligibleForHeidi2(billingInfo, gate.isMaxBillingSeat);
}
